import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Event as EventIcon,
  CalendarMonth,
  List as ListIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';
import { teacherAPI } from '../../services/api';
import EmptyState from '../../components/EmptyState';
import ScrollableTabs from '@components/mobile/ScrollableTabs';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const getMonthDays = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startPad = firstDay === 0 ? 6 : firstDay - 1;
  const days = [];
  for (let i = 0; i < startPad; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  return days;
};

const toDateStr = (year, month, day) => {
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
};

const TeacherHolidayCalendar = () => {
  const { user, logout } = useAuth();
  const currentYear = new Date().getFullYear();
  const [yearFilter, setYearFilter] = useState(currentYear);
  const [activeTab, setActiveTab] = useState(0); // 0=Calendar, 1=List
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await teacherAPI.getHolidays({ year: yearFilter });
      setHolidays(res.data.holidays || []);
    } catch {
      setError('Failed to load holidays. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [yearFilter]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const holidayMap = {};
  holidays.forEach(h => { holidayMap[String(h.holiday_date).substring(0, 10)] = h; });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const dateOnly = String(dateStr).substring(0, 10);
    const d = new Date(dateOnly + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  // ---- Calendar Grid View ----
  const CalendarView = () => (
    <Grid container spacing={2}>
      {MONTH_NAMES.map((monthName, monthIdx) => {
        const days = getMonthDays(yearFilter, monthIdx);
        return (
          <Grid item xs={12} sm={6} md={4} key={monthIdx}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                height: '100%'
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ mb: 1, color: 'primary.main', textAlign: 'center', fontSize: '0.85rem' }}
              >
                {monthName} {yearFilter}
              </Typography>

              <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" sx={{ mb: 0.5 }}>
                {DAY_LABELS.map(d => (
                  <Typography
                    key={d}
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      color: d === 'Sun' ? 'error.main' : 'text.secondary',
                      fontWeight: 600,
                      fontSize: '0.65rem'
                    }}
                  >
                    {d}
                  </Typography>
                ))}
              </Box>

              <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0.25}>
                {days.map((day, idx) => {
                  if (!day) return <Box key={idx} />;
                  const dateStr = toDateStr(yearFilter, monthIdx, day);
                  const holiday = holidayMap[dateStr];
                  const isSunday = (idx % 7) === 6;

                  return (
                    <Tooltip
                      key={idx}
                      title={holiday ? holiday.name : ''}
                      arrow
                      disableHoverListener={!holiday}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          aspectRatio: '1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          fontSize: '0.7rem',
                          fontWeight: holiday ? 700 : 400,
                          color: holiday
                            ? '#fff'
                            : isSunday
                            ? 'error.main'
                            : 'text.primary',
                          bgcolor: holiday ? 'primary.main' : 'transparent',
                        }}
                      >
                        {day}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );

  // ---- List View ----
  const ListView = () => (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
      {holidays.length === 0 ? (
        <EmptyState
          icon={EventIcon}
          title={`No holidays scheduled for ${yearFilter}`}
        />
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Holiday</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {holidays.map(h => (
                <TableRow key={h.id} hover>
                  <TableCell>
                    <Chip
                      label={formatDate(h.holiday_date)}
                      size="small"
                      sx={{ bgcolor: 'primary.50', color: 'primary.dark', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{h.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {h.description || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );

  return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
        {/* Page Header */}
        <Box mb={3}>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Holiday Calendar
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            View your school's holiday schedule
          </Typography>
        </Box>

        {/* Controls */}
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
          <Paper sx={{ p: 0.75 }}>
            <ScrollableTabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              TabIndicatorProps={{ sx: { display: 'none' } }}
              sx={{
                minHeight: 0,
                '& .MuiTabs-flexContainer': { gap: 1 },
                '& .MuiTab-root': {
                  minHeight: 0,
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  color: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2)
                  },
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }
                }
              }}
            >
              <Tab icon={<CalendarMonth fontSize="small" />} iconPosition="start" label="Calendar" />
              <Tab icon={<ListIcon fontSize="small" />} iconPosition="start" label="List" />
            </ScrollableTabs>
          </Paper>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={yearFilter}
              label="Year"
              onChange={(e) => setYearFilter(e.target.value)}
              sx={{ bgcolor: 'white' }}
            >
              {yearOptions.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Summary */}
        {!loading && !error && (
          <Box mb={2}>
            <Chip
              icon={<EventIcon />}
              label={`${holidays.length} holiday${holidays.length !== 1 ? 's' : ''} in ${yearFilter}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>
        )}

        {/* Content */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          activeTab === 0 ? <CalendarView /> : <ListView />
        )}
      </Container>
  );
};

export default TeacherHolidayCalendar;
